import { sortByDate } from 'src/utils/sorting';
import {
  type TriageAction,
  TriageActionGroup,
  TriageActionStatus,
  TriageActionVisibility,
  type TriageAttempt,
  type TriageRun,
  type TriageRunActionRaw,
  TriageRunProgress,
  type TriageRunRaw,
  type TriageRunStepRaw,
  TriageRunStepStatus,
} from './conversation-triage-run.types';
import {
  getActionGroupKey,
  getActionLabel,
  getActionPresentation,
  getActionReason,
} from './triage-action-presentation';

const STATUS_TO_GROUP: Record<TriageActionStatus, TriageActionGroup> = {
  [TriageActionStatus.Applied]: TriageActionGroup.Applied,
  [TriageActionStatus.Suggested]: TriageActionGroup.Suggested,
  [TriageActionStatus.Failed]: TriageActionGroup.Failed,
  [TriageActionStatus.Dismissed]: TriageActionGroup.Dismissed,
  [TriageActionStatus.InProgress]: TriageActionGroup.Suggested,
};

function resolveGroup(status: string): TriageActionGroup {
  return STATUS_TO_GROUP[status as TriageActionStatus] ?? TriageActionGroup.Suggested;
}

interface FlatAction {
  raw: TriageRunActionRaw;
  time: string;
  index: number;
}

function timeValue(iso: string): number {
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
}

function compareAttempts(a: FlatAction, b: FlatAction): number {
  return timeValue(a.time) - timeValue(b.time) || a.index - b.index;
}

function flattenActions(steps: TriageRunStepRaw[]): FlatAction[] {
  const flat: FlatAction[] = [];
  steps.forEach((step) => {
    (step.conversationTriageRunActions ?? []).forEach((raw) => {
      flat.push({ raw, time: raw.createdAt ?? step.createdAt, index: flat.length });
    });
  });
  return flat;
}

function groupKey(raw: TriageRunActionRaw): string {
  return getActionGroupKey(raw.payload) ?? `action:${raw.conversationTriageRunActionId}`;
}

function toAttempt(raw: TriageRunActionRaw, time: string): TriageAttempt {
  return {
    id: raw.conversationTriageRunActionId,
    status: raw.status as TriageActionStatus,
    applyError: raw.applyError ?? null,
    timestamp: time,
  };
}

function mapGroup(attempts: FlatAction[]): TriageAction {
  const ordered = [...attempts].sort(compareAttempts);
  const primary = ordered[ordered.length - 1];
  const raw = primary.raw;
  const { label, hasLabel } = getActionLabel(raw.display, raw.payload);
  const { icon, iconLibrary, accent, color } = getActionPresentation(raw.payload);

  return {
    id: raw.conversationTriageRunActionId,
    group: resolveGroup(raw.status),
    status: raw.status as TriageActionStatus,
    label,
    hasLabel,
    reason: getActionReason(raw.payload),
    icon,
    iconLibrary,
    accent,
    color,
    applyError: raw.applyError ?? null,
    attempts: ordered.length,
    history: ordered
      .slice(0, -1)
      .reverse()
      .map((attempt) => toAttempt(attempt.raw, attempt.time)),
  };
}

function groupActionsByTool(flat: FlatAction[]): TriageAction[] {
  const groups = new Map<string, FlatAction[]>();
  flat.forEach((entry) => {
    const key = groupKey(entry.raw);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  });
  return [...groups.values()].map(mapGroup);
}

function latestStep(steps: TriageRunStepRaw[]): TriageRunStepRaw | null {
  if (steps.length === 0) {
    return null;
  }
  // Descending by createdAt so the most recent step drives run progress.
  return [...steps].sort(sortByDate('createdAt', true))[0] ?? null;
}

// Run has no status column — derive it from the latest step (research D2).
function deriveProgress(steps: TriageRunStepRaw[]): TriageRunProgress {
  const step = latestStep(steps);

  switch (step?.status) {
    case TriageRunStepStatus.InProgress:
      return TriageRunProgress.Running;
    case TriageRunStepStatus.Error:
      return TriageRunProgress.Failed;
    case TriageRunStepStatus.Superseded:
      return TriageRunProgress.Superseded;
    default:
      return TriageRunProgress.Complete;
  }
}

export function mapTriageRun(raw: TriageRunRaw): TriageRun {
  const steps = raw.conversationTriageRunSteps ?? [];

  const visible = flattenActions(steps).filter(
    (entry) =>
      (entry.raw.visibility as TriageActionVisibility) !== TriageActionVisibility.Hide,
  );
  const visibleActions = groupActionsByTool(visible);

  const groups = {
    applied: visibleActions.filter((action) => action.group === TriageActionGroup.Applied),
    suggested: visibleActions.filter((action) => action.group === TriageActionGroup.Suggested),
    failed: visibleActions.filter((action) => action.group === TriageActionGroup.Failed),
    dismissed: visibleActions.filter((action) => action.group === TriageActionGroup.Dismissed),
  };

  const pendingSuggestedCount = visibleActions.filter(
    (action) => action.status === TriageActionStatus.Suggested,
  ).length;

  return {
    id: raw.conversationTriageRunId,
    conversationId: raw.conversationId,
    timestamp: latestStep(steps)?.createdAt ?? raw.createdAt,
    progress: deriveProgress(steps),
    groups,
    pendingSuggestedCount,
  };
}

export function mapTriageRuns(runs?: TriageRunRaw[] | null): TriageRun[] {
  return (runs ?? []).map(mapTriageRun);
}

export function triageRunHasVisibleActions(run: TriageRun): boolean {
  const { applied, suggested, failed, dismissed } = run.groups;
  return (
    applied.length + suggested.length + failed.length + dismissed.length > 0 ||
    run.progress === TriageRunProgress.Running
  );
}
